import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MagnifyingGlass, SquaresFour, Rows, Package, Eye, Storefront, ArrowSquareOut, Heart, Images } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { useOrg } from "@/context/OrgContext";
import { Input } from "@/components/ui/input";
import { PromoPageHeader } from "../components/PromoPageHeader";
import { KpiCard } from "../components/KpiCard";
import { EmptyState } from "../components/EmptyState";
import { CardsSkeleton } from "../components/Skeleton";
import { PromoSelect } from "../components/PromoSelect";
import { StatusBadge } from "../components/StatusBadge";
import { DataTable } from "../components/DataTable";
import { ProductCard } from "../components/wizard/ProductCard";
import { ProductDetailPanel } from "../components/catalog/ProductDetailPanel";
import { fetchProducts, useConnectionHealth } from "../usePromo";
import { MARKETPLACES } from "../platforms";
import { fmtDateTime, fmtRelative, productImages } from "../campaignUtils";

const skuOf = (p) => p.raw?.sku || p.external_id || p.id;
const catOf = (p) => p.raw?.category;
const favOf = (p) => (typeof p.raw?.num_favorers === "number" ? p.raw.num_favorers : null);
const dateOf = (p) => new Date(p.updated_at || p.first_synced_at || 0).getTime();

export default function ProductCatalog() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const { sources } = useConnectionHealth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [marketplace, setMarketplace] = useState("all");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("newest");
  const [view, setView] = useState("grid");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!currentOrg) return;
    let alive = true;
    setLoading(true);
    fetchProducts().then((list) => alive && setProducts(list || [])).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [currentOrg]);

  const marketplaceLabel = (key) => MARKETPLACES.find((m) => m.key === key)?.label || key;
  const hasFavorers = useMemo(() => products.some((p) => favOf(p) != null), [products]);

  const categories = useMemo(() => [...new Set(products.map(catOf).filter(Boolean))].sort(), [products]);
  const marketplaces = useMemo(() => [...new Set(products.map((p) => p.platform).filter(Boolean))].sort(), [products]);
  const statuses = useMemo(() => [...new Set(products.map((p) => p.status).filter(Boolean))].sort(), [products]);
  const lastSync = useMemo(() => {
    const ts = products.map(dateOf).filter(Boolean);
    return ts.length ? new Date(Math.max(...ts)).toISOString() : null;
  }, [products]);
  const connectedMk = useMemo(() => MARKETPLACES.filter((m) => sources.some((s) => s.connector_id === m.key && s.status === "connected")), [sources]);

  const filtered = useMemo(() => {
    let list = products.filter((p) => {
      if (marketplace !== "all" && p.platform !== marketplace) return false;
      if (category !== "all" && catOf(p) !== category) return false;
      if (status !== "all" && p.status !== status) return false;
      if (q) {
        const hay = `${p.title || ""} ${skuOf(p)} ${catOf(p) || ""} ${marketplaceLabel(p.platform)}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
    const cmp = {
      name: (a, b) => (a.title || "").localeCompare(b.title || ""),
      price: (a, b) => (a.price || 0) - (b.price || 0),
      newest: (a, b) => dateOf(b) - dateOf(a),
      oldest: (a, b) => dateOf(a) - dateOf(b),
      bestseller: (a, b) => (favOf(b) || 0) - (favOf(a) || 0),
    }[sort];
    return [...list].sort(cmp);
  }, [products, marketplace, category, status, q, sort]);

  const opt = (vals, allLabel) => [{ value: "all", label: allLabel }, ...vals];
  const sortOptions = [
    { value: "newest", label: t("promo.catalog.sort.newest") },
    { value: "oldest", label: t("promo.catalog.sort.oldest") },
    { value: "name", label: t("promo.catalog.sort.name") },
    { value: "price", label: t("promo.catalog.sort.price") },
    ...(hasFavorers ? [{ value: "bestseller", label: t("promo.catalog.sort.bestseller") }] : []),
  ];

  const tableCols = [
    { key: "marketplace", label: t("promo.catalog.col.marketplace"), render: (p) => {
      const mk = MARKETPLACES.find((m) => m.key === p.platform);
      return <span className="flex items-center gap-1.5">{mk && <mk.Icon size={13} />}{mk?.label || p.platform}</span>;
    } },
    { key: "product", label: t("promo.catalog.col.product"), render: (p) => <span className="font-medium">{p.title}</span> },
    { key: "category", label: t("promo.catalog.col.category"), render: (p) => catOf(p) || "—" },
    { key: "price", label: t("promo.catalog.col.price"), align: "right", render: (p) => p.price != null ? `${p.price} ${p.currency || ""}` : "—" },
    { key: "sku", label: t("promo.catalog.col.sku"), render: (p) => <span className="font-mono text-xs">{skuOf(p)}</span> },
    { key: "status", label: t("promo.catalog.col.status"), render: (p) => <StatusBadge status={p.status === "active" ? "connected" : p.status} label={p.status} /> },
    { key: "lastsync", label: t("promo.catalog.col.lastSync"), render: (p) => fmtRelative(p.updated_at || p.first_synced_at) },
    { key: "favorers", label: t("promo.catalog.col.favorers"), align: "right", render: (p) => favOf(p) != null ? <span className="flex items-center justify-end gap-1"><Heart size={11} weight="fill" />{favOf(p)}</span> : "—" },
    { key: "images", label: t("promo.catalog.col.images"), align: "right", render: (p) => <span className="flex items-center justify-end gap-1"><Images size={11} />{productImages(p).length}</span> },
    { key: "actions", label: t("promo.catalog.col.actions"), render: (p) => (
      <button onClick={(e) => { e.stopPropagation(); setSelected(p); }} className="rounded-md border border-border p-1.5 hover:bg-accent" data-testid={`table-view-${p.external_id || p.id}`}><Eye size={14} /></button>
    ) },
  ];

  return (
    <div className="space-y-6" data-testid="product-catalog">
      <PromoPageHeader overline={t("promo.nav.catalog")} title={t("promo.catalog.title")} description={t("promo.catalog.subtitle")} />

      {/* Header stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard index={0} testId="catalog-kpi-total" label={t("promo.catalog.kpi.total")} value={products.length} Icon={Package} />
        <KpiCard index={1} testId="catalog-kpi-marketplaces" label={t("promo.catalog.kpi.marketplaces")} value={connectedMk.length} Icon={Storefront}
          hint={connectedMk.map((m) => m.label).join(", ") || "—"} />
        <KpiCard index={2} testId="catalog-kpi-sync" label={t("promo.catalog.kpi.lastSync")} value={lastSync ? fmtRelative(lastSync) : "—"} Icon={ArrowSquareOut} hint={lastSync ? fmtDateTime(lastSync) : undefined} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("promo.catalog.search")} className="bg-card pl-9" data-testid="catalog-search" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-40"><PromoSelect value={marketplace} onChange={setMarketplace} testId="filter-marketplace"
            options={opt(marketplaces.map((mk) => ({ value: mk, label: marketplaceLabel(mk) })), t("promo.catalog.allMarketplaces"))} /></div>
          <div className="w-40"><PromoSelect value={category} onChange={setCategory} testId="filter-category"
            options={opt(categories.map((c) => ({ value: c, label: c })), t("promo.catalog.allCategories"))} /></div>
          <div className="w-36"><PromoSelect value={status} onChange={setStatus} testId="filter-status"
            options={opt(statuses.map((s) => ({ value: s, label: s })), t("promo.catalog.allStatus"))} /></div>
          <div className="w-40"><PromoSelect value={sort} onChange={setSort} options={sortOptions} testId="filter-sort" /></div>
          <div className="flex overflow-hidden rounded-md border border-border">
            <button onClick={() => setView("grid")} className={cn("p-2 transition-colors", view === "grid" ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50")} data-testid="view-grid"><SquaresFour size={16} /></button>
            <button onClick={() => setView("table")} className={cn("border-l border-border p-2 transition-colors", view === "table" ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50")} data-testid="view-table"><Rows size={16} /></button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <CardsSkeleton count={8} />
      ) : products.length === 0 ? (
        <EmptyState Icon={Package} title={t("promo.catalog.emptyTitle")} description={t("promo.catalog.emptyDesc")}
          action={<Link to="/ecommerce" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" data-testid="catalog-sync-cta">{t("promo.catalog.syncCta")}</Link>} />
      ) : filtered.length === 0 ? (
        <EmptyState Icon={MagnifyingGlass} title={t("promo.catalog.noMatch")} description={t("promo.catalog.noMatchDesc")} />
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="catalog-grid">
          {filtered.map((p) => (
            <ProductCard key={p.external_id || p.id} product={p} showMarketplace onClick={() => setSelected(p)} testId={`catalog-card-${p.external_id || p.id}`} />
          ))}
        </div>
      ) : (
        <DataTable columns={tableCols} rows={filtered} rowKey="id" onRowClick={setSelected} testId="catalog-table" />
      )}

      <ProductDetailPanel product={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
